import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { PhoneInputComponent } from '../phone-input/phone-input.component';
import { NgxSmartModalComponent } from 'ngx-smart-modal';
import { forkJoin, interval, Subscription } from 'rxjs';
import { BusinessService } from '../../core/business/business.service';
import { BusinessPhoneNumber } from '../../core/business/business';
import { ConfigurationHelperService } from '../../configuration/configuration-helper.service';
import { UserService } from '../../core/user/user.service';
import { formatPhoneNumber } from '../../core/helpers/utils';

export type TestNumbersLayout = 'chips' | 'modal-only';

// Test-number management shared by the guided setup (Test / legacy Go Live
// steps, via the "Manage Test Numbers" modal) and the Launch Instructions tab
// (chips + inline add, ENG-2292 Figma 19580-71365). Owns the 3-slot form
// array, the save/delete calls and the retry poll while no number exists.
@Component({
    selector: 'rosie-test-numbers',
    templateUrl: './test-numbers.component.html',
    styleUrls: ['./test-numbers.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TestNumbersComponent implements OnInit, OnDestroy {
    // 'chips' renders the chip list with inline add; 'modal-only' renders just
    // the modal and lets the host display the numbers its own way.
    @Input() layout: TestNumbersLayout = 'chips';
    // Poll every 15s while no test number exists (the API captures the first
    // caller's number automatically).
    @Input() pollWhileEmpty = false;
    // Chips layout: the "Only these test numbers..." lock note above the chips.
    @Input() showLockNote = true;
    @Input() maxNumbers = 3;
    @Output() numbersChanged = new EventEmitter<BusinessPhoneNumber[]>();

    @ViewChild('testNumbersModal') testNumbersModal: NgxSmartModalComponent;
    @ViewChild('addPhoneInput') addPhoneInput: PhoneInputComponent;

    testNumbersFormArray: FormArray;
    draftTestNumbersFormArray: FormArray;
    displayTestNumbers: BusinessPhoneNumber[] = [];
    isAdding = false;
    isSavingNewNumber = false;
    addNumberForm: FormGroup;

    private retrySubscription: Subscription;

    constructor(
        private businessService: BusinessService,
        private configHelper: ConfigurationHelperService,
        private user: UserService,
        private fb: FormBuilder
    ) { }

    get activeNumbers(): BusinessPhoneNumber[] {
        return this.displayTestNumbers.filter((number) => number?.number?.trim()?.length > 0);
    }

    get hasTestNumber(): boolean {
        return this.activeNumbers.length > 0;
    }

    get hasMultipleTestNumbers(): boolean {
        return this.activeNumbers.length > 1;
    }

    get canAddMore(): boolean {
        return this.activeNumbers.length < this.maxNumbers;
    }

    get canSaveNewNumber(): boolean {
        const control = this.addNumberForm?.get('number');
        const digits = formatPhoneNumber(control?.value || '');
        return !!control && !control.invalid && digits?.length === 10 && !this.isSavingNewNumber;
    }

    ngOnInit(): void {
        this.initializeTestNumbersFormArray();
        this.updateDisplayTestNumbers();
        this.refreshTestNumbers();
        this.startRetryIfNeeded();
    }

    ngOnDestroy(): void {
        this.stopRetry();
    }

    // --- Chips layout --------------------------------------------------------

    startAdd(): void {
        this.addNumberForm = this.configHelper.createBusinessPhoneNumber(undefined, true);
        this.isAdding = true;
        // Put the cursor in the new field so typing can start right away.
        setTimeout(() => this.addPhoneInput?.phoneInput?.nativeElement?.focus());
    }

    cancelAdd(): void {
        this.isAdding = false;
        this.addNumberForm = null;
    }

    saveNewNumber(): void {
        if (!this.canSaveNewNumber) {
            this.addNumberForm?.get('number')?.markAsTouched();
            return;
        }
        this.isSavingNewNumber = true;
        this.businessService.updatePhoneNumber(this.addNumberForm.value).subscribe({
            next: () => {
                this.isSavingNewNumber = false;
                this.cancelAdd();
                this.refreshTestNumbers();
            },
            error: () => this.isSavingNewNumber = false
        });
    }

    removeNumber(phoneNumber: BusinessPhoneNumber, event?: Event): void {
        event?.stopPropagation();
        if (!phoneNumber?.id) {
            return;
        }
        this.businessService.updatePhoneNumber({ ...phoneNumber, number: '' }).subscribe(() => this.refreshTestNumbers());
    }

    trackByNumber(index: number, phoneNumber: BusinessPhoneNumber): string {
        return phoneNumber?.id || String(index);
    }

    // --- Modal layout (legacy Test / Go Live steps) --------------------------

    openTestNumbersModal(): void {
        this.createDraftTestNumbersFormArray();
        this.testNumbersModal?.open();
    }

    createDraftTestNumbersFormArray(): void {
        const formGroups = [];
        this.testNumbersFormArray.controls.forEach(control => {
            formGroups.push(this.configHelper.createBusinessPhoneNumber(control.value));
        });
        this.draftTestNumbersFormArray = this.fb.array(formGroups);
    }

    hasTestNumberValue(index: number): boolean {
        const control = this.draftTestNumbersFormArray?.at(index);
        const number = control?.get('number')?.value;
        return number && number.trim().length > 0;
    }

    removeTestNumber(index: number): void {
        const control = this.draftTestNumbersFormArray?.at(index);
        if (control) {
            control.get('number')?.setValue('');
        }
    }

    saveTestNumbers(): void {
        const hasInvalidNumbers = this.draftTestNumbersFormArray.controls.some(control => {
            const numberControl = control.get('number');
            const phoneNumber = numberControl?.value?.trim();
            if (!phoneNumber) {
                return false;
            }
            return numberControl?.invalid;
        });

        if (hasInvalidNumbers) {
            this.draftTestNumbersFormArray.controls.forEach(control => {
                control.get('number')?.markAsTouched();
            });
            return;
        }

        this.draftTestNumbersFormArray.controls.forEach((draftControl, index) => {
            const originalControl = this.testNumbersFormArray.at(index);
            const draftValue = draftControl.value;
            const originalValue = originalControl.value;
            if (JSON.stringify(draftValue) !== JSON.stringify(originalValue)) {
                this.savePhoneNumber(draftValue, index);
            }
        });

        this.testNumbersFormArray.controls.forEach((control, index) => {
            const draftValue = this.draftTestNumbersFormArray.at(index).value;
            control.patchValue(draftValue, { emitEvent: false });
        });

        this.updateDisplayTestNumbers();
        this.testNumbersModal?.close();
    }

    handleTestNumbersModalClose(): void {
        this.draftTestNumbersFormArray = null;
    }

    // --- Data ----------------------------------------------------------------

    refreshTestNumbers(): void {
        this.businessService.retrieveTestNumbers().subscribe(() => {
            this.initializeTestNumbersFormArray();
            this.updateDisplayTestNumbers();
            if (this.hasTestNumber || this.user.hasCreditCardOnFile) {
                this.stopRetry();
            }
        });
    }

    updateDisplayTestNumbers(): void {
        this.displayTestNumbers = this.testNumbersFormArray?.value || [];
        this.numbersChanged.emit(this.activeNumbers);
    }

    private initializeTestNumbersFormArray(): void {
        const formGroups = [];
        this.businessService.businessTestNumbers.forEach(phoneNumber => formGroups.push(this.configHelper.createBusinessPhoneNumber(phoneNumber)));
        while (formGroups.length < this.maxNumbers) {
            formGroups.push(this.configHelper.createBusinessPhoneNumber());
        }
        this.testNumbersFormArray = this.fb.array(formGroups);
    }

    private startRetryIfNeeded(): void {
        if (this.pollWhileEmpty && !this.hasTestNumber && !this.retrySubscription) {
            this.retrySubscription = interval(15000).subscribe(() => this.refreshTestNumbers());
        }
    }

    private stopRetry(): void {
        if (this.retrySubscription) {
            this.retrySubscription.unsubscribe();
            this.retrySubscription = null;
        }
    }

    // Editing an existing slot is a delete + create pair (the API has no PUT
    // path in this flow); a fresh slot is a plain create.
    private savePhoneNumber(phoneNumberValue: BusinessPhoneNumber, index: number): void {
        if (phoneNumberValue.id) {
            forkJoin([
                this.businessService.updatePhoneNumber({ ...phoneNumberValue, number: '' }),
                this.businessService.updatePhoneNumber({ ...phoneNumberValue, id: null })
            ]).subscribe(([, updatedNumber]: [BusinessPhoneNumber, BusinessPhoneNumber]) => {
                this.testNumbersFormArray.at(index).patchValue({ id: updatedNumber?.id }, { emitEvent: false });
                this.updateDisplayTestNumbers();
            });
        } else {
            this.businessService.updatePhoneNumber(phoneNumberValue).subscribe((phoneNumber: BusinessPhoneNumber) => {
                this.testNumbersFormArray.at(index).patchValue({ id: phoneNumber?.id }, { emitEvent: false });
                this.updateDisplayTestNumbers();
            });
        }
    }
}
